import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import { getMediaUrl } from '../../services/media';
import styles from './Catalog.module.css';
import { FaSearch, FaTimes, FaFilter } from 'react-icons/fa';

const LIMIT = 24;

const Catalog = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const loaderRef = useRef(null);

  const [filters, setFilters] = useState({
    genre: searchParams.get('genre') || '',
    title: searchParams.get('search') || '',
  });
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  useEffect(() => {
    api.get('/genres').then((res) => {
      setGenres(res.data?.genres || []);
    }).catch(() => {});
  }, []);

  const fetchBooks = useCallback((currentPage, currentFilters, append = false) => {
    if (currentPage === 1) setInitialLoading(true);
    else setLoading(true);

    const params = { page: currentPage, limit: LIMIT };
    if (currentFilters.genre) params.genre = currentFilters.genre;
    if (currentFilters.title) params.search = currentFilters.title;

    api.get('/books', { params })
      .then((res) => {
        const { books: data = [], total: t = 0, page: p = 1 } = res.data;
        setBooks((prev) => append ? [...prev, ...data] : data);
        setHasMore(p * LIMIT < t);
      })
      .catch(() => {
        if (!append) setBooks([]);
        setHasMore(false);
      })
      .finally(() => {
        setInitialLoading(false);
        setLoading(false);
      });
  }, []);

  // При смене фильтров — сброс и загрузка с 1 страницы
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchBooks(1, filters, false);

    const urlParams = new URLSearchParams();
    if (filters.genre) urlParams.set('genre', filters.genre);
    if (filters.title) urlParams.set('search', filters.title);
    setSearchParams(urlParams);
  }, [filters]);

  // При смене страницы (кроме 1) — дозагрузка
  useEffect(() => {
    if (page === 1) return;
    fetchBooks(page, filters, true);
  }, [page]);

  // IntersectionObserver на loader-элемент
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !initialLoading) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, initialLoading]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ genre: '', title: '' });
  };

  return (
    <div className={styles.catalog}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t('catalog.title')}</h1>
        <p className={styles.subtitle}>{t('home.subtitle')}</p>

        {/* Search bar */}
        <div className={styles.searchRow}>
          <div className={styles.searchWrapper}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              value={filters.title}
              onChange={(e) => handleFilterChange('title', e.target.value)}
              className={styles.searchInput}
              placeholder={t('home.search')}
            />
          </div>
          <button className={styles.filterToggle} onClick={() => setFilterSheetOpen(true)}>
            <FaFilter />
            {t('catalog.filter')}
            {filters.genre && <span className={styles.filterBadge} />}
          </button>
        </div>

        {/* Genre buttons */}
        <div className={styles.genreRow}>
          <button
            className={`${styles.genreBtn} ${filters.genre === '' ? styles.genreBtnActive : ''}`}
            onClick={() => handleFilterChange('genre', '')}
          >
            Все
          </button>
          {genres.map((g) => (
            <button
              key={g.id}
              className={`${styles.genreBtn} ${filters.genre === String(g.id) ? styles.genreBtnActive : ''}`}
              onClick={() => handleFilterChange('genre', String(g.id))}
            >
              {g.name}
            </button>
          ))}
          {(filters.genre || filters.title) && (
            <button className={styles.resetButton} onClick={handleReset}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* Books grid */}
        {initialLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : books.length === 0 ? (
          <div className={styles.empty}>{t('catalog.noBooks')}</div>
        ) : (
          <div className={styles.booksGrid}>
            {books.map((book) => (
              <Link to={`/book/${book.id}`} key={book.id} className={styles.bookCard}>
                <div className={styles.bookPoster}>
                  {book.coverUrl ? (
                    <img src={getMediaUrl(book.coverUrl)} alt={book.title} className={styles.bookCover} />
                  ) : (
                    <div className={styles.bookIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                <h3 className={styles.bookTitle} title={book.title}>{book.title}</h3>
              </Link>
            ))}
          </div>
        )}

        {/* Infinite scroll trigger */}
        <div ref={loaderRef} className={styles.loaderTrigger}>
          {loading && <div className={styles.loadingMore}>{t('common.loading')}</div>}
          {!hasMore && books.length > 0 && (
            <p className={styles.endMessage}>Все книги загружены</p>
          )}
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filterSheetOpen && (
        <div className={styles.sheetOverlay} onClick={() => setFilterSheetOpen(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sheetHandle} />
            <div className={styles.sheetHeader}>
              <h3 className={styles.sheetTitle}>{t('catalog.genres')}</h3>
              <button className={styles.sheetClose} onClick={() => setFilterSheetOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.sheetOptions}>
              <button
                className={`${styles.sheetOption} ${filters.genre === '' ? styles.sheetOptionActive : ''}`}
                onClick={() => { handleFilterChange('genre', ''); setFilterSheetOpen(false); }}
              >
                {t('catalog.allGenres')}
              </button>
              {genres.map((g) => (
                <button
                  key={g.id}
                  className={`${styles.sheetOption} ${filters.genre === String(g.id) ? styles.sheetOptionActive : ''}`}
                  onClick={() => { handleFilterChange('genre', String(g.id)); setFilterSheetOpen(false); }}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;
